import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { cloneDeep, isEmpty } from 'lodash';
import { toast } from 'material-react-toastify';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import {
  FilterValue,
  getEmptyFilterValue,
} from '../../interfaces/ProductFilter/FilterValue';
import { Waiting } from '../../components/Waiting/Waiting';
import { createFilterValue, updateFilterValue } from '../../api/ProductFilter';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { translateContent } from '../../api/Translation';

type UpsertFilterValueDialogProps = {
  open: boolean;
  onUpsert: () => void;
  onClose: () => void;
  keyID: number;
  selectedLocale: string | null;
  clickedValue?: FilterValue;
};

export const UpsertFilterValueDialog = (
  props: UpsertFilterValueDialogProps,
) => {
  const [loading, setLoading] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [currentState, setCurrentState] = useState(getEmptyFilterValue());

  const isEditing = !!props.clickedValue;
  const resolvedTitle = props.selectedLocale
    ? currentState.titleTranslation[props.selectedLocale] ?? ''
    : currentState.title;

  useEffect(() => {
    setCurrentState(
      props.clickedValue
        ? cloneDeep(props.clickedValue)
        : getEmptyFilterValue(),
    );
  }, [props.clickedValue]);

  const handleTranslate = () => {
    if (!props.selectedLocale) return;

    setTranslating(true);
    translateContent(currentState.title, props.selectedLocale)
      .then((translation) =>
        setCurrentState({
          ...currentState,
          titleTranslation: {
            ...currentState.titleTranslation,
            [props.selectedLocale!]: translation,
          },
        }),
      )
      .catch((err) => toast.error(err.message))
      .finally(() => setTranslating(false));
  };

  const handleConfirm = () => {
    setLoading(true);
    if (isEditing) {
      updateFilterValue(currentState)
        .then(() => {
          toast.success('Filtre değeri başarıyla güncellendi');
          props.onUpsert();
          props.onClose();
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      createFilterValue(props.keyID, currentState)
        .then(() => {
          toast.success('Filtre değeri başarıyla oluşturuldu');
          props.onUpsert();
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const validState = !isEmpty(resolvedTitle);

  const handleTextFieldKeyDown = (e: any) => {
    if (!validState) {
      return;
    }

    if (e.keyCode === 13) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <Waiting open={loading} />
      <DialogTitle sx={{ ...sharedStyles.h6, color: sharedColors.gray6 }}>
        {isEditing ? 'Değeri Düzenle' : 'Yeni Değer'}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          component='div'
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            mt: 1.5,
            gap: 1.5,
            alignItems: 'center',
            width: 350,
          }}
        >
          <TextField
            label='Değer'
            value={resolvedTitle}
            placeholder={props.selectedLocale ? currentState.title : undefined}
            disabled={translating}
            onChange={(e) =>
              setCurrentState(
                props.selectedLocale
                  ? {
                      ...currentState,
                      titleTranslation: {
                        ...currentState.titleTranslation,
                        [props.selectedLocale]: e.target.value,
                      },
                    }
                  : { ...currentState, title: e.target.value },
              )
            }
            onKeyDown={handleTextFieldKeyDown}
            size='small'
            error={isEmpty(resolvedTitle)}
            fullWidth
          />

          {!!props.selectedLocale && (
            <>
              <Button
                variant='outlined'
                startIcon={<AutoAwesomeIcon />}
                onClick={handleTranslate}
                sx={{ ...sharedStyles.buttonText }}
              >
                Çevir
              </Button>

              {translating && <CircularProgress size={24} />}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={props.onClose}
          sx={sharedStyles.buttonText}
        >
          İptal
        </Button>
        <Button
          variant='contained'
          color='primary'
          disabled={!validState}
          onClick={handleConfirm}
          sx={sharedStyles.buttonText}
        >
          Onayla
        </Button>
      </DialogActions>
    </Dialog>
  );
};
